import { setAssetOwnershipModal, showSuccessAlert } from "@actions/modals";
import { mapToSelect } from "@api/egm";
import ownership from "@api/ownership";
import { getOptionTotal, getOptionValue } from "@helpers/options";
import { default as React, useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Button, Grid, Header, Input, Modal, Select } from "semantic-ui-react";
import ScrollableList from "../shared/ScrollableList";
import OptionsList from "../systemAdmin/OptionsList";

const AssetOwnershipProfileModal = (props) => {
  const { setAssetOwnershipModal, showSuccessAlert } = props;
  const {
    open,
    profiles,
    selectedProfile,
    types,
    ownershipTypeId,
    operator,
    fields,
    name,
    loading,
  } = props.modal;
  const nameFieldRef = useRef(null);

  const onChange = async (option, value) => {
    const newFields = [
      ...fields.map((field) => {
        return field.code === option.code ? { ...option, value } : field;
      }),
    ];

    setAssetOwnershipModal({ fields: newFields });
  };

  const resetForm = useCallback(() => {
    setAssetOwnershipModal({
      selectedProfile: { id: null },
      name: "",
      fields: [],
      ownershipTypeId: null,
    }).then(() => nameFieldRef.current?.focus());
  }, [setAssetOwnershipModal]);

  const onProfileClick = (profile) => {
    const selectedProfile = profiles.find((x) => x.id === profile.value);
    setAssetOwnershipModal({ selectedProfile }).then(() => {
      ownership.getProfileAttributes(selectedProfile.id).then(
        (fields) => setAssetOwnershipModal({ fields, loading: false }),
        () => setAssetOwnershipModal({ loading: false }),
      );
    });
  };

  const updateProfiles = useCallback(() => {
    setAssetOwnershipModal({ loading: true }).then(() => {
      ownership.getProfiles(operator).then(
        (profiles) => setAssetOwnershipModal({ profiles, loading: false }),
        () => setAssetOwnershipModal({ loading: false }),
      );
    });
  }, [setAssetOwnershipModal, operator]);

  const onSaveClick = () => {
    const saveFn = selectedProfile.id
      ? ownership.saveProfile
      : ownership.createProfile;
    const verb = selectedProfile.id ? "updated" : "created";
    saveFn(operator, name, ownershipTypeId, fields, selectedProfile?.id).then(
      (selectedProfile) => {
        showSuccessAlert(
          `Profile ${verb} successfully!`,
          "Success",
          async () => {
            setAssetOwnershipModal({ selectedProfile }).then(() =>
              updateProfiles(),
            );
          },
        );
      },
    );
  };

  useEffect(() => {
    if (!open || !selectedProfile) {
      return;
    }
    const { name, ownershipTypeId } = selectedProfile;
    setAssetOwnershipModal({ name, ownershipTypeId });
  }, [selectedProfile, setAssetOwnershipModal, open]);

  useEffect(() => {
    if (!open || !operator) {
      return;
    }

    resetForm();
    updateProfiles();
    ownership
      .getTypes(operator)
      .then((types) => setAssetOwnershipModal({ types }));
  }, [operator, open, setAssetOwnershipModal, resetForm, updateProfiles]);

  useEffect(() => {
    if (!open || !ownershipTypeId || selectedProfile.id !== null) {
      return;
    }

    setAssetOwnershipModal({ loading: true }).then(() => {
      ownership.getTypeAttributes(ownershipTypeId).then(
        (fields) => setAssetOwnershipModal({ fields, loading: false }),
        () => setAssetOwnershipModal({ loading: false }),
      );
    });
  }, [selectedProfile, ownershipTypeId, open, setAssetOwnershipModal]);

  const canSubmit = () => {
    if (fields.length < 1) return false;
    if (!ownershipTypeId) return false;
    if (!name || name.length < 1) return false;

    let canSubmit = true;

    fields.forEach((option) => {
      if (option.type === 13 && option.isRequired) {
        const total = getOptionTotal(option, fields);
        if (option.minValue > total || option.maxValue < total) {
          canSubmit = false;
        }
      }

      if (option.isRequired && !getOptionValue(option) && option.type !== 13) {
        canSubmit = false;
      }
    });

    return canSubmit;
  };

  const optionsPrefix = (
    <>
      <Grid.Row key="access_profile">
        <Grid.Column width="7">
          Profile Type <span style={{ color: "#db2828" }}>*</span>
        </Grid.Column>
        <Grid.Column width="9">
          <Select
            fluid
            autoFocus={selectedProfile.id === null}
            value={ownershipTypeId}
            disabled={selectedProfile.id !== null}
            options={types}
            onChange={(e, { value }) => {
              setAssetOwnershipModal({ ownershipTypeId: value });
            }}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row key="profile_name">
        <Grid.Column width="7">
          Profile Name <span style={{ color: "#db2828" }}>*</span>
        </Grid.Column>
        <Grid.Column width="9">
          <Input
            required
            fluid
            value={name}
            onChange={(e, { value }) => setAssetOwnershipModal({ name: value })}
          />
        </Grid.Column>
      </Grid.Row>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={() => setAssetOwnershipModal({ open: false })}
      size="large"
      closeIcon
    >
      <Modal.Header>Asset Ownership Profile</Modal.Header>
      <Modal.Content>
        <Grid>
          <Grid.Row>
            <Grid.Column width="5" stretched>
              <ScrollableList
                loading={loading}
                items={mapToSelect(profiles)}
                value={selectedProfile.id}
                onClick={onProfileClick}
              />
            </Grid.Column>
            <Grid.Column width="11">
              <Header className="mt-0_5">
                {!selectedProfile.id ? "New Profile" : selectedProfile.name}
              </Header>
              <OptionsList
                loading={loading}
                options={fields}
                prefix={optionsPrefix}
                onChange={onChange}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <Button
          positive
          content="New"
          floated="left"
          icon="plus"
          disabled={!operator}
          onClick={() => resetForm()}
        />
        <Button
          positive
          content="Save"
          icon="save"
          disabled={!operator || !canSubmit()}
          onClick={() => onSaveClick()}
        />
      </Modal.Actions>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  return {
    modal: state.assetOwnershipProfileModal,
  };
};

export default connect(mapStateToProps, {
  setAssetOwnershipModal,
  showSuccessAlert,
})(AssetOwnershipProfileModal);
